import React, { useEffect, useState } from 'react';
import { omit } from 'lodash';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AttribsFields from '../theme/forms/AttribsFields';
import ScriptEditSidebarForm from './ScriptEditSidebarForm';

const VoterRoutedForm = ({ element, attribsField, onSubmit, onCancel, onDeleteElement }) => {
  const [formData, setFormData] = useState({ paths: [], connectors: [], attribs: {}, ...element });

  useEffect(() => {
    if (!element.connectors) return;
    const newData = { ...formData };
    element.connectors.forEach((c) => {
      if (c.rules) {
        newData.paths.push({ pathName: c.value_text, pathWeight: c.rules[0].weight || 0 });
      } else {
        newData.paths.push({ pathName: '', pathWeight: 0 });
      }
    });
    setFormData(newData);
  }, [element]);

  function submit() {
    const newElement = { ...element, ...omit(formData, ['paths']) };
    if (!formData.paths) formData.paths = [];
    if (!newElement.connectors) newElement.connectors = [];
    newElement.connectors = formData.paths.map((p, i) => {
      const conn = newElement.connectors.find((c) => c.ordinal_position === i + 1);
      if (!conn) {
        return {
          key: [i, element.element_hash].join('-'),
          ordinal_position: i + 1,
          value_text: p.pathName,
          rules: [{ weight: p.pathWeight }],
        };
      }
      if (!conn.rules) conn.rules = [];
      conn.value_text = p.pathName;
      if (Array.isArray(conn.rules)) {
        const rule = conn.rules.find((r) => typeof r.weight !== 'undefined');
        rule.weight = p.pathWeight;
      } else {
        conn.rules = [{ weight: p.pathWeight }];
      }
      return conn;
    });
    onSubmit(newElement);
  }

  const handleAttribsChange = (attribs) => {
    const next =
      attribs.check_prior_voter_selection === false
        ? { ...attribs, allow_selection_of_prior_selected_voter: false }
        : attribs;
    setFormData({ ...formData, attribs: next });
  };

  const handlePathChange = (index, field, value) => {
    const updatedPaths = [...formData.paths];
    updatedPaths[index][field] = value;
    setFormData({ ...formData, paths: updatedPaths });
  };

  const addPath = () => {
    setFormData({
      ...formData,
      paths: [...formData.paths, { pathName: '', pathWeight: '' }],
    });
  };

  const removePath = (index) => {
    const updatedPaths = formData.paths.filter((_, i) => i !== index);
    setFormData({ ...formData, paths: updatedPaths });
  };

  return (
    <ScriptEditSidebarForm
      title="Add a Voter Routed element"
      iconClass="fa fa-random"
      displayName={formData.display_name || ''}
      onDisplayNameChange={(val) => setFormData({ ...formData, display_name: val })}
      showPrompt={false}
      onSubmit={submit}
      onCancel={onCancel}
      onDeleteElement={onDeleteElement}
      showDelete
    >
      <Stack spacing={1.25}>
        {!!attribsField && (
          <AttribsFields
            fields={attribsField}
            value={formData.attribs}
            onChange={handleAttribsChange}
          />
        )}

        {formData.paths.map((path, index) => (
          <Stack key={index} spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                Path {index + 1}
              </Typography>
              {formData.paths.length > 1 && (
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`Remove path ${index + 1}`}
                  onClick={() => removePath(index)}
                >
                  <Box component="i" className="fas fa-minus-circle" sx={{ fontSize: '0.9rem' }} />
                </IconButton>
              )}
            </Stack>
            <TextField
              fullWidth
              size="small"
              label="Path Name"
              value={path.pathName}
              onChange={(e) => handlePathChange(index, 'pathName', e.target.value)}
              variant="outlined"
            />
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Weight (%)"
              value={path.pathWeight}
              onChange={(e) => handlePathChange(index, 'pathWeight', e.target.value)}
              variant="outlined"
            />
          </Stack>
        ))}

        <Button
          size="small"
          color="secondary"
          onClick={addPath}
          sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0 }}
        >
          Add Path
        </Button>
      </Stack>
    </ScriptEditSidebarForm>
  );
};

export default VoterRoutedForm;
